import { Injectable } from '@angular/core';
import { IAuthDto, IUserDto } from 'core/dto';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { controllerEndpoints } from '../controller.endpoints';
import joinUrls from '../utils.ts/join-urls';
import { ICrudBasicMethods } from './types/i-crud-basic.methods';

export interface ExtendedUserDto {
  id: string
  email: string
  firstName: string
  lastName: string
  mobile: string
  role: string,
  delete: boolean,
  active: boolean
}

@Injectable({
  providedIn: 'root'
})
export class UserRemoteControllerService implements Pick<ICrudBasicMethods<IUserDto>, 'getOne' | 'create' | 'update' | 'remove'> {

  private readonly _endpoint = controllerEndpoints.USER
  private readonly GET_BY_EMAIL = 'getByEmail';
  private readonly OIDC_LOGIN = 'oidcLogin';
  private readonly EMAIL_AUTH = 'auth'

  private readonly DEACTIVATE = 'deactivate';
  private readonly CHANGE_PASSWORD = 'password';
  private readonly GET_BY_CRITERIA = 'getByCriteria';
  private readonly SSO = 'sso';
  private readonly GET_BY_JWT = 'getByJwt';
  private readonly IMPORT_FROM_AD = 'importFromAD';

  constructor(
    private readonly _apiService: ApiService
  ) {

  }


  getOne(id: string) {

    const getOneEndpoint = joinUrls(this._endpoint, id)
    return this._apiService.get<IUserDto>(getOneEndpoint)
  }

  getByEmail(email: string) {
    const getByEmailEndpoint = joinUrls(this._endpoint, this.GET_BY_EMAIL, email);
    return this._apiService.get<IUserDto>(getByEmailEndpoint);
  }

  getMany(loadOptions: { filter: string, pageNo: number, pageSize: number, sortBy: string }) {

    const getManyEndpoint = joinUrls(this._endpoint, 'users');
    if (!loadOptions.filter) {
      delete loadOptions.filter
    }
    else {
      loadOptions.filter = loadOptions.filter.toLowerCase();
    }


    return this._apiService.get<IUserDto[]>(getManyEndpoint, loadOptions)
  }

  oidcLogin(params?: { activityId?: string, pluginName?: string, accessedRoutePath?: string }): Observable<{ redirectUri: string }> {
    const { accessedRoutePath, activityId, pluginName } = params || {}
    const oidcEndpoint = joinUrls(this._endpoint, this.OIDC_LOGIN)
    return this._apiService.get(oidcEndpoint, { accessedRoutePath, activityId, pluginName });
  }


  emailAuth(params: { email: string, password: string }): Observable<IAuthDto> {
    const emailAuthEndpoint = joinUrls(this._endpoint, this.EMAIL_AUTH)
    return this._apiService.post(emailAuthEndpoint, params);
  }

  getUsersByCriteria(params: { page: number, size: number, firstName: string, lastName: string, email: string, phone: string, isEligibleTeamMember?: boolean, sort: string[] }) {


    return this._apiService.get<{ data: ExtendedUserDto[], totalCount: number }>(joinUrls(this._endpoint, this.GET_BY_CRITERIA), params)
    // return new Observable<{ data: ExtendedUserDto[], totalCount: number }>(subscriber => {

    //   setTimeout(() => {
    //     subscriber.next({ data: users.filter((_, idx) => idx >= skip && idx <= (skip + take)), totalCount: users.length })
    //     subscriber.complete();
    //   },
    //     500
    //   )
    // })
  }

  changePassword(id: string, password: string) {

    return this._apiService.put<IUserDto>(joinUrls(this._endpoint, this.CHANGE_PASSWORD, id), { id, password });
  }

  setPassword(id: string, password: string) {

    return this._apiService.put<IUserDto>(joinUrls(this._endpoint, this.CHANGE_PASSWORD, id), { password });
  }

  create(createDto: Partial<IUserDto>): Observable<IUserDto> {

    return this._apiService.post<IUserDto>(this._endpoint, createDto);
  }

  update(id: string, updateDto: Partial<IUserDto>): Observable<IUserDto> {

    return this._apiService.put<IUserDto>(joinUrls(this._endpoint, id), updateDto,);
  }

  remove(id: string) {
    return this._apiService.put<IUserDto>(joinUrls(this._endpoint, this.DEACTIVATE, id));
  }

  getCurrentUser() {

    return this._apiService.get<IUserDto>(joinUrls(this._endpoint, this.GET_BY_JWT))
  }

  sso(): Observable<IAuthDto> {

    return this._apiService.get(joinUrls(this._endpoint, this.SSO))
  }

  importFromAd() {
    return this._apiService.get(joinUrls(this._endpoint, this.IMPORT_FROM_AD));
  }
}
